import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  Box,
  Button,
  Wizard,
  WizardStep,
  WizardStepRenderParams,
} from '@factorialco/deprecated-design-system'
import {
  AccountsListPageDocument,
  BankingAccountLegalEntityFragment,
  ConnectBankDocument,
  CreateBankingManualAccountDocument,
  CurrencySelectorCurrencyFragment,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useCallback, useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { defaultBankNumberFormat } from 'modules/banking/lib/bankAccountNumbersHelpers'
import { getDefaultAlias } from 'modules/banking/utils'

import { WizardAccountData, WizardAutomaticAccountData, WizardManualAccountData } from './types'
import { BankAccountConnectionWizardStep } from './wizardSteps/BankAccountConnectionWizardStep'
import { BankAccountManualInformationWizardStep } from './wizardSteps/BankAccountManualInformationWizardStep'
import { BankAccountSelectorWizardStep } from './wizardSteps/BankAccountSelectorWizardStep'
import { LedgerAccountLinkStep } from './wizardSteps/LedgerAccountLinkStep'
import { LegalEntityWizardStep } from './wizardSteps/LegalEntityWizardStep'
import { MembershipsStep } from './wizardSteps/MembershipsStep'

const isWizardAutomaticAccountData = (
  data: WizardAccountData
): data is WizardAutomaticAccountData => {
  return data.institution !== null && data.institution !== undefined
}

interface NewAccountModalProps {
  onClose: () => void
  legalEntities: BankingAccountLegalEntityFragment[]
  currencies: CurrencySelectorCurrencyFragment[]
  accountingEnabled: boolean
}

export const NewAccountModal = ({
  onClose,
  legalEntities,
  currencies,
  accountingEnabled,
}: NewAccountModalProps) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const [showConnectStep, setShowConnectStep] = useState<boolean>(false)
  const [showConnectionModal, setShowConnectionModal] = useState<boolean>(false)
  const [onboardingUrl, setOnboardingUrl] = useState<string | null>(null)
  const [accountNumberErrors, setAccountNumberErrors] = useState<{
    accountNumber?: string
    complementaryData?: string
  }>({})

  const handleClose = (id: number) => {
    if (accountingEnabled) {
      navigate(navigation.finance.banking.accounts.accountId.fullPath({ id }))
    } else {
      onClose()
      navigate(navigation.finance.banking.accounts.fullPath())
    }
  }

  const trackAddAccount = (account_type: 'manual' | 'automatic', membershipsIds?: number[]) => {
    track(trackingEvents.accounting.banking.addBankAccount, { account_type })
    if (membershipsIds && membershipsIds.length > 0)
      track(trackingEvents.accounting.banking.addPeopleBankAccount)
  }

  const isBankAccountDataCompleted = useCallback((wizardData: WizardAccountData): boolean => {
    if (isWizardAutomaticAccountData(wizardData)) {
      return !!wizardData.institution && !!wizardData.legalEntityId
    }
    const validNumber = !!wizardData.accountNumber && !!wizardData.accountNumberType
    return validNumber && !!wizardData.legalEntityId && !!wizardData.currency
  }, [])

  const [createManualAccount] = useMutation(CreateBankingManualAccountDocument, {
    extractorFn: (data) => data.bankingMutations.createManualBankingBankAccount,
    refetchQueries: [AccountsListPageDocument],
  })

  const [connectBank] = useMutation(ConnectBankDocument, {
    extractorFn: (data) => data.bankingMutations.createForExternalBankBankingOnboarding,
    refetchQueries: [AccountsListPageDocument],
  })

  const redirectToBankAggregationProvider = useUnsafeCallback(() => {
    if (!onboardingUrl) return
    window.open(onboardingUrl, '_self')
  }, [onboardingUrl])

  const handleSubmit = async (data: WizardAccountData) => {
    if (isWizardAutomaticAccountData(data) && data.legalEntityId) {
      app.setProgress({
        message: i18n.t('banking.accounts.new_wizard.notifications.connecting_bank'),
      })

      setOnboardingUrl(null)

      const result = await connectBank({
        variables: {
          legalEntityId: data.legalEntityId,
          institutionId: data.institution.id,
        },
      }).then((res) => {
        trackAddAccount('automatic')
        return res
      })

      const handled = handleResult(result, () => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('banking.accounts.new_wizard.notifications.error'),
        })
        app.setProgress(null)
      })

      if (handled.success) {
        const newOnboardingUrl = handled.data.onboarding?.onboardingUrl
        if (newOnboardingUrl) {
          setOnboardingUrl(newOnboardingUrl)
          setShowConnectionModal(true)
          // The disableFocusTrap is not working now, so we automatically redirect
          setTimeout(() => {
            setShowConnectionModal(false)
            window.open(newOnboardingUrl, '_self')
          }, 2000)
          app.setProgress(null)
        }
      }
    } else {
      app.setProgress({
        message: i18n.t('banking.accounts.new_wizard.notifications.creating'),
      })

      const result = await createManualAccount({
        variables: {
          // eslint-disable-next-line no-type-assertion/no-type-assertion
          legalEntityId: data.legalEntityId! + '',
          currency: data.currency,
          accountNumber: data.accountNumber || '',
          accountNumberType: data.accountNumberType,
          complementaryData: data.complementaryData,
          accountAlias: data.accountAlias,
          ledgerAccountId: data.ledgerAccountId,
          bankAccountMembershipAccessIds: data.membershipsIds,
        },
      }).then((res) => {
        trackAddAccount('manual', data.membershipsIds)
        return res
      })

      const handled = handleResult(result, () => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('banking.accounts.new_wizard.notifications.error'),
        })
        app.setProgress(null)
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('banking.accounts.new_wizard.notifications.success'),
        })
        app.setProgress(null)

        if (handled.data.bankAccount) {
          handleClose(handled.data.bankAccount.id)
        }
      }
    }
  }

  const legalEntityStep: WizardStep<WizardAccountData> = useUnsafeMemo(
    () => ({
      title: i18n.t('banking.accounts.new_wizard.step_1.title'),
      isCompleted: (wizardData) => !!wizardData.legalEntityId,
      nextText: i18n.t('banking.accounts.new_wizard.next'),
      render: (props) => (
        <LegalEntityWizardStep
          {...(props as WizardStepRenderParams<WizardManualAccountData>)}
          legalEntities={legalEntities}
        />
      ),
    }),
    [legalEntities]
  )

  const bankAccountSelectorStep: WizardStep<WizardAccountData> = useUnsafeMemo(
    () => ({
      title: i18n.t('banking.accounts.new_wizard.step_2.title'),
      isCompleted: () => Boolean(true),
      nextText: i18n.t('banking.accounts.new_wizard.next'),
      render: (props) => (
        <BankAccountSelectorWizardStep onSelect={(inst) => setShowConnectStep(!!inst)} {...props} />
      ),
    }),
    []
  )

  const bankAccountManualInformationStep: WizardStep<WizardManualAccountData> = useUnsafeMemo(
    () => ({
      title: i18n.t('banking.accounts.new_wizard.step_3_0.title'),
      isCompleted: (data) =>
        isBankAccountDataCompleted(data) &&
        accountNumberErrors.accountNumber === undefined &&
        accountNumberErrors.complementaryData === undefined,
      nextText: i18n.t('banking.accounts.new_wizard.next'),
      render: (props) => (
        <BankAccountManualInformationWizardStep
          {...props}
          currencies={currencies}
          errors={accountNumberErrors}
          onErrorsChange={setAccountNumberErrors}
        />
      ),
    }),
    [currencies, isBankAccountDataCompleted, accountNumberErrors]
  )

  const bankAccountConnectionStep: WizardStep<WizardAutomaticAccountData> = useUnsafeMemo(
    () => ({
      title: i18n.t('banking.accounts.new_wizard.step_3_1.title'),
      isCompleted: isBankAccountDataCompleted,
      nextText: i18n.t('banking.accounts.new_wizard.finish_and_synchronize'),
      render: (props) => <BankAccountConnectionWizardStep {...props} />,
    }),
    [isBankAccountDataCompleted]
  )

  const ledgerAccountLinkStep: WizardStep<WizardManualAccountData> = useUnsafeMemo(
    () => ({
      title: i18n.t('banking.accounts.finish_wizard.step_2.side_title'),
      isCompleted: isBankAccountDataCompleted,
      nextText: i18n.t('banking.accounts.new_wizard.next'),
      render: (props) => <LedgerAccountLinkStep {...props} />,
    }),
    [isBankAccountDataCompleted]
  )

  const membershipsStep: WizardStep<WizardManualAccountData> = useUnsafeMemo(
    () => ({
      title: i18n.t('banking.accounts.finish_wizard.step_3.side_title'),
      isCompleted: isBankAccountDataCompleted,
      nextText: i18n.t('banking.accounts.new_wizard.create_account'),
      render: (props) => <MembershipsStep {...props} />,
    }),
    [isBankAccountDataCompleted]
  )

  const steps = useUnsafeMemo(() => {
    if (accountingEnabled) {
      return showConnectStep
        ? [legalEntityStep, bankAccountSelectorStep, bankAccountConnectionStep]
        : [
            legalEntityStep,
            bankAccountSelectorStep,
            bankAccountManualInformationStep,
            ledgerAccountLinkStep,
            membershipsStep,
          ].filter((n) => n)
    } else {
      return [legalEntityStep, bankAccountManualInformationStep, membershipsStep]
    }
  }, [
    showConnectStep,
    legalEntityStep,
    bankAccountSelectorStep,
    bankAccountConnectionStep,
    bankAccountManualInformationStep,
    accountingEnabled,
    ledgerAccountLinkStep,
    membershipsStep,
  ])

  const defaultLegalEntity = legalEntities.length === 1 ? legalEntities[0] : undefined

  const initialData = useUnsafeMemo<WizardAccountData>(
    () => ({
      legalEntityId: defaultLegalEntity?.id ?? null,
      currency: defaultLegalEntity?.currency ?? 'EUR',
      accountNumberType: defaultBankNumberFormat(defaultLegalEntity?.country || 'es'),
      accountNumber: '',
      accountAlias: getDefaultAlias('finance'),
      complementaryData: '',
      institution: null,
      maxDays: 0,
      search: '',
      country: defaultLegalEntity?.country.toLocaleUpperCase() ?? null,
      ledgerAccountId: null,
      ledgerAccountName: null,
    }),
    [defaultLegalEntity]
  )

  return (
    <>
      {showConnectionModal && (
        <Modal
          title={i18n.t('banking.accounts.new_wizard.step_3_1.external_redirection')}
          description={i18n.t('banking.accounts.new_wizard.step_3_1.extra_info')}
          illustration={'workflow'}
          locked
          footer={
            <Box flexDirection="row" gap="s8">
              <Button
                disabled={onboardingUrl === null}
                onClick={() => redirectToBankAggregationProvider()}
              >
                {i18n.t('common.continue')}
              </Button>
            </Box>
          }
          disableFocusTrap
          onClose={() => null}
        />
      )}
      <Wizard.Modal
        title={i18n.t('banking.accounts.actions.add_account')}
        icon="add-block"
        description=""
        initialData={initialData}
        defaultStepIndex={legalEntities.length === 1 ? 1 : 0}
        onSubmit={handleSubmit}
        onClose={onClose}
        steps={steps as WizardStep<WizardAccountData>[]}
      />
    </>
  )
}
